
import React, { useEffect, useState } from 'react';
import { Row, Col, Alert, Badge, CloseButton } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Card from '../../../components/Card';

import { LocationItem } from '../../../features/locations';
import { getLocations, getPropertyInfo } from '../../../services/api/location';
import { ApiError, Location } from '../../../services/openapi';

import { useKeycloak } from '@react-keycloak/web';

import SearchInput from '../../../components/SearchInput';
import ListPagination from '../../../components/ListPagination';
import RefreshPull from '../../../components/RefreshPull';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useTranslation } from 'react-i18next'; 

const Locations = () => {
    const [locations, setLocations] = useState<Location[]>([]);        
    const [locationCount, setLocationCount] = useState<string>("0");    
    const [pagingString, setPagingString] = useState<string>("0-25");    
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [tableBodyHeight, setTableBodyHeight] = useState<number>(380);
    const [formData, setFormData] = useLocalStorage<string>("locations", "formData", "");
    const [searchData, setSearchData] = useLocalStorage<Record<string, string>>("locations","searchData", {});
    const [filterData, setFilterData] = useLocalStorage<string>("locations","filterData", "");
    const [currentPage, setCurrentPage] = useLocalStorage<number>("locations","currentPage", 0);
    const [siteFilter, setSiteFilter] = useLocalStorage<number[]>("sites","siteFilter", []);
    const [siteFilterQuery, setSiteFilterQuery] = useLocalStorage<string>("sites", "siteFilterQuery", "");
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [selectedId, setSelectedId] = useState(null);
    const [rowClickCount, setRowClickCount] = useState({});
    const [newLocation, setNewLocation] = useState<Location>();

    const { keycloak, initialized } = useKeycloak();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleFilter = (e: string): void => {
        setLocations([]);
        setFilterData(e);
        setCurrentPage(0);
    }

    const handleSearch = (e: string): void => {
        setLocations([]);
        searchData["search"] = e;
        setSearchData(searchData);
        formData === "" ? setFormData(" ") : setFormData("");       
        setCurrentPage(0);
    }

    const handleSearchColumn1 = (e: string): void => {
        setLocations([]);
        searchData["description"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn2 = (e: string): void => {
        setLocations([]);
        searchData["vmcCount"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn3 = (e: string): void => {
        setLocations([]);
        searchData["terminalCount"] = e;
        setSearchData(searchData);
        handleChange();
    }    

    const handleRowClick = (tx) => {
        if (window.matchMedia('(pointer: coarse)').matches) {
            setRowClickCount({ [tx.id]: 1 });
        }
        
        setRowClickCount((prev) => {
            const newCount = (prev[tx.id] || 0) + 1;
            if (newCount === 1) {
            setSelectedId(tx.id);
            return { ...prev, [tx.id]: newCount };
            } else if (newCount === 2) {
            setSelectedId(null);
            return { ...prev, [tx.id]: 0 };
            }
            return prev;
        });
    };

    const handleRefreshButton = (): void => {
        setLocations([]);
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const delay = async (ms: number) => {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    let timer: NodeJS.Timeout | null = null;

    const submitSearch = () => {
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleChange = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(submitSearch, 2000);
        setCurrentPage(0);
    }

    const handlePaging = async (e: number) => {
        errorMsg.length = 0;

        do {
            await delay(1000);
        } while (isRefreshing);       

        setCurrentPage(e);

        const start = ((e + 1) * 25) - 25;
        const end = ((e + 1) * 25);
        const rangeString = `${start}-${end}`
        setPagingString(rangeString);

        document.querySelector('#refresh')?.classList.remove("d-none");
        setIsRefreshing(true);        

        getLocations(keycloak.token || '', rangeString, { search: formData, Online: filterData, sites: siteFilter.join(), sitesQuery: siteFilterQuery }, searchData)
            .then((result) => {
                setLocations(result.body);
                var totalCount = result.header.split('/')[1];
                setLocationCount(totalCount);
                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
                let l: Location = {
                    id: 0,
                    description: "",
                    siteId: "0",
                    vmcCount: 0,
                    terminalCount: 0,
                    locationId: 0,
                    hidden: false,
                    propertyNames: result?.body[0]?.propertyNames
                } as unknown as Location;

                if (!l?.propertyNames) {
                    getPropertyInfo(keycloak.token || '').then((res: any) => {
                        l.propertyNames = res;
                        l.siteId = res?.siteId?.predefinedValues ? res.siteId.predefinedValues[0] : "";
                        setNewLocation(l);
                    }).catch((ex: any) => setNewLocation(l));                    
                } else {
                    setNewLocation(l);
                }
            })
            .catch((error: ApiError) => {
                try {
                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                } catch (ex) {
                    errorMsg.push("Error handling response");
                    setErrorMsg(errorMsg);
                }

                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            });
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>): boolean => {
        if (event.key === 'Enter') {
            event.preventDefault();
            return false;
        }
        return true;
    }

    const init = () => {
        document.querySelector('.errorMsg')?.classList.add("d-none");
        handlePaging(currentPage ?? 0);
        initSearchFields();
    };

    const initSearchFields = () => {
        Object.keys(searchData).map(x => {
            const search: HTMLInputElement | null = document.querySelector(`.${x}`) as HTMLInputElement;
            if (search) {
                search.value = searchData[x];
            }
        })
    }

    const onResize = () => {
        var e = document.querySelector(".vertical-scrollable") as HTMLElement;
        var h = window.innerHeight;
        e.style.height = (h - 284) + "px";
        e.style.maxHeight = (h - 280) + "px";
        setTableBodyHeight(h - 284);
    };

    useEffect(() => {
        window.addEventListener("resize", onResize);
        onResize();
        if (locations?.length === 0) {
            init();
        }
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (locations.length > 0) return;
        init();
    }, [formData, filterData]);

    return (
        <>
            <Row className="">                  
                <Col className="">
                    <Card className="rounded">                        
                        <Card.Body className="">
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="">
                                <Col sm="12" className="">    
                                    <h4 className="mb-2 qvend-title">{t("locaties")}</h4>                                    
                                </Col>
                            </Row>
                            <form className="" onKeyDown={handleKeyDown}>
                                <Row style={{ fontSize: '11px' }}>
                                    <Col md={3} xs={5} style={{ paddingRight: 0 }}>
                                        <div className="bd-example">
                                            <div>
                                                <SearchInput className="form-control search" type="search" placeholder={t("search")} onChange={handleSearch} value={searchData.search ?? ""} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={3} xs={1}>
                                        <button type="button" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.3rem' }}>...</button>
                                    </Col>                                    
                                    <Col className="text-end">
                                        { hasSites(newLocation) ? (
                                            <Link to="/dashboard/details/location" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.35rem 0.45rem', marginRight: '8px' }} onClick={() => { 
                                                let siteIdValue: string = "";
                                                if (newLocation && (newLocation as any).propertyNames && (newLocation as any).propertyNames["siteId"] && (newLocation as any).propertyNames["siteId"]["predefinedValues"]) {
                                                    let values = (newLocation as any).propertyNames["siteId"]["predefinedValues"];
                                                    if (values.length === 1) {
                                                        siteIdValue = values[0];
                                                    }
                                                }
                                                newLocation && (newLocation.siteId = siteIdValue ? siteIdValue : (siteFilter.length === 1 ? siteFilter.pop()!.toString() : "0")); localStorage.setItem('Location', JSON.stringify(newLocation));
                                            }}>+</Link>
                                        ) : (
                                            <a className="btn btn-qvend" style={{ pointerEvents: 'none', color: 'gray', backgroundColor: 'lightgray', cursor: 'default', fontSize: '11px', padding: '0.35rem 0.45rem', marginRight: '8px' }}
                                                onClick={(e) => e.preventDefault()}
                                            >+</a>
                                        ) }
                                        <button type="button" className="btn btn-qvend" style={{ fontSize: '11px' }}>Download</button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12} xs={10} style={{ paddingRight: 0, fontSize: '11px' }} className="form-control-sm">
                                        {((siteFilter && siteFilter.length > 0) || (siteFilterQuery && siteFilterQuery.length > 2)) ? <span className="text-end">
                                            <Badge pill bg="dark" className="p-1 " onClick={() => { setSiteFilter([]); setSiteFilterQuery(""); setLocations([]); setCurrentPage(0); formData === "" ? setFormData(" ") : setFormData(""); window.localStorage.removeItem("searchDataSites") }}><CloseButton variant="white" /> Sites</Badge></span> : null}
                                </Col>
                                </Row>
                            </form>
                            <Row>
                                <Col sm="12" xs="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={handlePaging} />
                                    <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                        <div>
                                            <Row className="qvend-table-font" style={{ background: "#2b2e83", color: "#FFFFFF", overflowY: "scroll" }}>
                                                <Col xl={8} lg={8} md={6} sm={6} xs={9} className="qvend-table table-column-first">{t('locations.location')}</Col>
                                                <Col xl={2} lg={2} md={3} sm={0} xs={0} className="qvend-table d-none d-md-block">{t('locations.vmcCount')}</Col>
                                                <Col xl={2} lg={2} md={3} sm={6} xs={3} className="qvend-table">{t('locations.terminalCount')}</Col>                                                
                                            </Row>                                            
                                        </div>
                                        <div className="vertical-scrollable" style={{ overscrollBehaviorY: 'contain' }}>
                                            <Row className={("qvend-table-font")} style={{ background: "#cfd5ea", color: "black" }}>
                                                <Col xl={8} lg={8} md={6} sm={6} xs={9} className="qvend-table search-in-row-first"><SearchInput className="form-control description" type="search" onChange={handleSearchColumn1} value={searchData.description ?? ""} /></Col>
                                                <Col xl={2} lg={2} md={3} sm={0} xs={0} className="qvend-table search-in-row-padding d-none d-md-block"><SearchInput className="form-control vmcCount" type="search" onChange={handleSearchColumn2} value={searchData.vmcCount ?? ""} /></Col>
                                                <Col xl={2} lg={2} md={3} sm={6} xs={3} className="qvend-table search-in-row-padding"><SearchInput className="form-control terminalCount" type="search" onChange={handleSearchColumn3} value={searchData.terminalCount ?? ""} /></Col>
                                            </Row>
                                            <Row className="d-none errorMsg">
                                                <Col>
                                                    {
                                                        errorMsg.map((item, index) => {
                                                            return <Alert key={index} variant="warning" className="alert" role="alert">
                                                                <span>{item}</span>
                                                            </Alert>;
                                                        })
                                                    }
                                                </Col>
                                            </Row>
                                                {locations.map((loc: Location, index) => (
                                                    <div onClick={() => handleRowClick(loc)} key={loc.id}>
                                                    <LocationItem                                                        
                                                        key={loc.id}
                                                        location={loc}
                                                        rowColor={selectedId === loc.id ? "gray" : index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                    />
                                                    </div>
                                                ))}
                                            {
                                                //Array.from({ ...dummyVmc, length: (tableBodyHeight/16.5) - vmcs.length } as VmcStatus[]).map((vmc: VmcStatus, index) => (
                                                //    <VmcStatusItem
                                                //        key={index+vmcs.length }
                                                //        vmcStatus={dummyVmc}
                                                //        rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                //        className=""
                                                //    />
                                                //))
                                            }
                                        </div>                                        
                                    </div>
                                </Col>                              
                            </Row>
                            <ListPagination
                                articlesCount={locationCount}
                                currentPage={currentPage}
                                onSetPage={handlePaging}
                                onRefresh={handleRefreshButton } />
                        </Card.Body>
                    </Card>
                </Col>                                    
            </Row>
        </>
    )
}

export default Locations

function hasRole(item: any, key: string, keycloak: any) {
    if (!(item && item.propertyNames && item.propertyNames[key])) {
        return false;
    }

    const prop = item.propertyNames[key];
    const roleNames = prop ? prop.roleNames : null;
    if (roleNames === null || roleNames === undefined) return true;
    let result: boolean = false;
    if (!keycloak) return false;
    roleNames.forEach((role: string) => { result = result || keycloak.hasResourceRole(role); });
    return result;
}

function hasSites(item: any) {
    if (!(item && item.propertyNames && item.propertyNames["siteId"] && item.propertyNames["siteId"]["predefinedValues"])) {
        return false;
    }

    const vals = item.propertyNames["siteId"]["predefinedValues"];
    if (!Array.isArray(vals)) return false;
    if (vals.length === 0) return false;
    return true;
}
